import { FunctionComponent, useEffect, useState } from "react";
import { Button, Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/20/solid";

type ModalProps = {
  title: string;
  showButton?: boolean;
  buttonText?: string;
  width?: "max-w-3xl" | "max-w-lg";
  children?: JSX.Element | JSX.Element[];
  open?: boolean;
  closeable?: boolean;
  onClose?: () => void;
  onClick?: () => void;
};

export const Modal: FunctionComponent<ModalProps> = ({
  open: isOpen,
  title,
  children,
  onClose,
  onClick,
  width = "max-w-lg",
  buttonText,
  closeable = true,
  showButton = true,
}: ModalProps) => {
  const [open, setOpen] = useState(isOpen);

  // legacy implementation - the modal should be able to close itself without interaction from the parent component
  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  const close = () => {
    setOpen(false);
    onClose && onClose();
  };

  const onButtonClick = () => {
    setOpen(false);
    onClick && onClick();
  };

  return (
    <Dialog
      open={open}
      as="div"
      className="relative z-10 focus:outline-none"
      onClose={() => closeable && close()}
    >
      <DialogBackdrop className="fixed inset-0 bg-slate-500 bg-opacity-75 transition-opacity" />
      <div className="fixed z-20 inset-0 overflow-y-auto text-base font-sans text-neutral-700">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel
            transition
            className={`relative top-1/2 max-h-[85vh] md:max-h-full overflow-auto inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left shadow-xl transform transition-all sm:my-8 sm:align-middle ${width} w-full sm:p-6`}
          >
            {closeable && (
              <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  onClick={close}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
            )}
            <DialogTitle as="h3" className="text-2xl mb-6">
              {title}
            </DialogTitle>
            {children}
            {showButton && (
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <Button
                  className="bg-primary w-full md:w-80 px-8 py-2 mt-4 text-white rounded text-center font-serif font-bold cursor-pointer no-underline focus:outline-none"
                  onClick={onButtonClick}
                >
                  {buttonText}
                </Button>
              </div>
            )}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};
